import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { Modal, FormLayout, Tabs, Card } from '@shopify/polaris';
import { Formik } from 'formik';
import BasicTextField from '@presentational/BasicTextField';
import { tAttribute } from '@utils';
import * as Yup from 'yup';

import BannerGeneralForm from './BannerGeneralForm';
import BannerB2BForm from './BannerB2BForm';

const BannerSchema = Yup.object().shape({
  title: Yup.string().required('requerido'),
  url: Yup.string().required('requerido').url("debe ser un link válido"),
  alt: Yup.string().nullable(),
  url_web: Yup.string().url("debe ser un link válido").nullable(),
  b2b_url: Yup.string().url("debe ser un link válido").nullable(),
  identifier: Yup.string().required('requerido').matches(/^[a-zA-Z0-9-]*$/, "formato inválido"),
});

const propTypes = {
  banner: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,

  errors: PropTypes.array,
  title: PropTypes.string.isRequired,
  ctaTitle: PropTypes.string.isRequired,
  fetchCollectionURL: PropTypes.string.isRequired,
  collectionBaseURL: PropTypes.string.isRequired,
  fetchProductURL: PropTypes.string.isRequired,
}

const defaultProps = {
  open: false,
}

const resourceTypes = { COLLECTION: "collection", PRODUCT: "product" } // Matching those on Banner model

const BannerFormModal = (props) => {
  const [tabSelected, setTabSelected] = useState(0);

  const tabs = [
    { id: "general-banner", content: "General", accessibilityLabel: "General" },
    { id: "b2b-banner", content: "B2B", accessibilityLabel: "B2B" },
  ]

  const handleSubmit = (values, formikBag) => {
    props.handleSubmit(values);
    formikBag.resetForm()
  }

  // TODO use useFormik when upgrade to 2.0
  // subhandler for collection
  // subhandler for product
  const handleShopifyResourceSelection = (selectedValue, selectedItem, formikProps, resourceType) => {
    const url = resourceType == resourceTypes.COLLECTION ? `${props.collectionBaseURL}/${selectedItem.handle}` : selectedItem.onlineStoreUrl;
    const newValues = { 
      ...formikProps.values, 
      url, // diff for product
      title: !!formikProps.values.title ?  formikProps.values.title : selectedItem.title,
      shopify_resource_id: selectedValue[0],
      shopify_resource_type: resourceType,
    }
    formikProps.setValues(newValues);
  }

  const handleUrlChange = (_id, value, formikProps) => {
    formikProps.setValues({
      ...formikProps.values, 
      url: value,
      shopify_resource_id: null, // If manually change ID then this gets nulled
      shopify_resource_type: "not_linked",
    });
  }

  // imageField can be image_desktop or image_mobile
  const handleImageDrop = (acceptedFiles, formikProps, imageFieldName) => {
    const removeHandle = `remove_${imageFieldName}`
    const uploadHandle = `${imageFieldName}_upload`
    formikProps.setValues({
      ...formikProps.values,
      [uploadHandle]: acceptedFiles[0],
      [removeHandle]: null,
    })
  }

  const handleImageRemove = (formikProps, imageFieldName) => {
    const removeHandle = `remove_${imageFieldName}`;
    const uploadHandle = `${imageFieldName}_upload`;
    const previewHandle = `${imageFieldName}_preview`;
    formikProps.setValues({
      ...formikProps.values,
      [previewHandle]: null, // to display
      [uploadHandle]: null,
      [removeHandle]: true,
    })
  }

  const handleTabChange = useCallback( (selectedTabIndex) => setTabSelected(selectedTabIndex),  [], );

  // existingImage: props.banner.image_url ? { url: props.banner.image_url } : null
  return (
    <Formik
      initialValues={{
        ...props.banner,
        image_desktop_preview: props.banner.image_desktop_url ? { url: props.banner.image_desktop_url } : null,
        image_mobile_preview: props.banner.image_mobile_url ? { url: props.banner.image_mobile_url } : null,

        b2b_image_desktop_preview: props.banner.b2b_image_desktop_url ? { url: props.banner.b2b_image_desktop_url } : null,
        b2b_image_mobile_preview: props.banner.b2b_image_mobile_url ? { url: props.banner.b2b_image_mobile_url } : null,
      }}
      onSubmit={ handleSubmit }
      validationSchema={ BannerSchema }
      enableReinitialize
    >
      { (formikProps) => 
        <Modal
          open={ props.open }
          large
          title={ props.title }
          onClose={ props.onClose }

          primaryAction={{
            content: props.ctaTitle,
            onAction: formikProps.handleSubmit,
            disabled: !formikProps.isValid,
          }}
          secondaryActions={{
            content: I18n.t("actions.cancel"),
            onAction: props.onClose,
          }}
        >
          <Modal.Section>
            <FormLayout>
              {/* { console.log("Modal",formikProps.values) } */}
              {/* { console.log("Modal errors",formikProps.errors) } */}
              <BasicTextField 
                name="title"
                placeholder="Main xxx"
                label={ tAttribute("general", "title") }
                handleChange={ (value, id) => formikProps.setFieldValue(id, value) }
                values={ formikProps.values }
                errors={ formikProps.errors }
              />

              <BasicTextField 
                name="identifier"
                placeholder="main-xxx"
                label={ tAttribute("banner", "identifier") }
                handleChange={ (value, id) => formikProps.setFieldValue(id, value) }
                values={ formikProps.values }
                errors={ formikProps.errors }
              />

              <Card>
                <Tabs tabs={tabs} selected={tabSelected} onSelect={handleTabChange} fitted>
                  <Card.Section>
                    { tabSelected == 0 &&
                      <BannerGeneralForm
                        formikProps={ formikProps }
                        resourceTypes={ resourceTypes }
                        fetchCollectionURL={ props.fetchCollectionURL }
                        fetchProductURL={ props.fetchProductURL }
                        handleShopifyResourceSelection={ handleShopifyResourceSelection }
                        handleUrlChange={ handleUrlChange }
                        handleImageRemove={ handleImageRemove }
                        handleImageDrop={ handleImageDrop }
                      />
                    }

                    { tabSelected == 1 &&
                      <BannerB2BForm
                        collectionBaseURL={ props.collectionBaseURL }
                        formikProps={ formikProps }
                        resourceTypes={ resourceTypes }
                        fetchCollectionURL={ props.fetchCollectionURL }
                        fetchProductURL={ props.fetchProductURL }
                        handleUrlChange={ handleUrlChange }
                        handleImageRemove={ handleImageRemove }
                        handleImageDrop={ handleImageDrop }
                      />
                    }
                    
                  </Card.Section>
                </Tabs>
              </Card>

            </FormLayout>
          </Modal.Section>
        </Modal>
      }
    </Formik>
  )
};

BannerFormModal.propTypes = propTypes;
BannerFormModal.defaultProps = defaultProps;

export default BannerFormModal;